<template>
    <div
        id="sportsFooterEl"
        v-bind:style="sportsFooterStyle"
        class="bottomFunction-cont"
    >
        <Tooltip v-if="showCashOutTooltip" class="tooltips_cashout ASI_JARVIS_Message_Btn_PromoCashOut">
            <p v-on:click="clickCashOut">
                <SvgIcon class="tooltip-cashOut-icon" iconName="sportIcon_cashOut" />
                <span>{{ i18n.t('betSlip_cashOutTooltip') }}</span>
            </p>
        </Tooltip>
        <JackpotWidget v-if="isShowJackpotWidget" isInBottomFunction />
        <MiniSingleBetSlip v-if="isShowMiniSingleModeBetSlip" />
        <MiniParlayBetSlip v-else-if="isShowMiniParlayBetSlip" />
        <MiniBetBuilderBetSlip v-else-if="isShowMiniBetBuilderBetSlip" />
        <div class="bottomFunction">
            <div
                class="bottomFunction-item"
                v-bind:class="[{
                    _disabled: !isMixParlayMode,
                    _active: isShowMiniBetSlip || isShowMediumBetSlip,
                    ASI_JARVIS_Footer_Btn_ParlaySlip: !isMixParlayMode,
                    ASI_JARVIS_Footer_Btn_ParlayList: isMixParlayMode,
                }, 'MiniParlaySlip_tab']"
                v-on:click="clickParlayBetSlip"
            >
                <Transition name="fade-out">
                    <Tooltip
                        v-if="!isMiniMpTicketEnabled && showTooltip"
                        class="tooltips_left"
                        v-bind:text="isEuroSite ? 'betSlip_parlayTooltip_EURO' : 'betSlip_parlayTooltip'"
                    />
                </Transition>
                <div class="tooltip-icon">
                    <SvgIcon iconName="sportIcon_betSlip" />

                    <Transition enterActiveClass="bounce">
                        <div
                            v-if="!isMiniMpTicketEnabled && ticketCount > 0"
                            v-bind:key="ticketCount"
                            class="count"
                        >
                            {{ ticketCount }}
                        </div>
                    </Transition>
                </div>
                <p>
                    {{ i18n.t(isEuroSite ? 'bottom_function_bet_slip_EURO' : 'bottom_function_bet_slip') }}
                </p>
            </div>
            <div
                class="bottomFunction-item ASI_JARVIS_Footer_Btn_MyEvent"
                v-bind:class="{
                    _active: $route.name === RoutePage.MyMatches,
                }"
                v-on:click="goTo(RoutePage.MyMatches, '/my-matches')"
            >
                <SvgIcon iconName="sportIcon_myEvent" />
                <p>{{ i18n.t('bottom_function_my_matches') }}</p>
            </div>
            <div
                v-if="shouldShowDepositButton"
                v-bind:id="depositButtonId"
                class="bottomFunction-item ASI_JARVIS_Footer_Btn_Deposit"
                v-bind:data-gtm="depositDataGtm"
                v-on:click="clickDeposit"
            >
                <SboIcon name="system:depositColor" size="20" />
                <p>{{ i18n.t('comm_deposit_button') }}</p>
            </div>
            <div
                class="bottomFunction-item ASI_JARVIS_Footer_Btn_MyBet"
                v-bind:class="{
                    _active: $route.name === RoutePage.MyBets,
                }"
                v-on:click="goTo(RoutePage.MyBets, '/my-bets')"
            >
                <div class="tooltip-icon">
                    <SvgIcon iconName="sportIcon_myBets" />

                    <div v-if="isShowRunningBetCount" class="count">
                        {{ runningBetCount }}
                    </div>
                </div>
                <p>{{ i18n.t('bottom_function_my_bets') }}</p>
            </div>
            <div
                v-if="isShowCashOutPage"
                class="bottomFunction-item ASI_JARVIS_Footer_Btn_CashOut"
                v-bind:class="{
                    _active: $route.name === RoutePage.CashOut,
                }"
                v-bind:data-copromo="hasSeenCashOutTutorial ? 'T' : 'F'"
                v-on:click="clickCashOut"
            >
                <div class="tooltip-icon">
                    <img
                        v-if="!hasSeenCashOutTutorial || isCashOutAnimating"
                        class="cashout-icon"
                        src="@/assets/images/cashout-icon-animation.gif"
                        alt=""
                        aria-hidden="true"
                    >
                    <img
                        v-else-if="$route.name !== RoutePage.CashOut"
                        class="cashout-icon"
                        src="@/assets/images/cashout-icon.png"
                        alt=""
                        aria-hidden="true"
                    >
                    <img
                        v-else-if="$route.name === RoutePage.CashOut"
                        class="cashout-icon"
                        src="@/assets/images/cashout-icon-active.png"
                        alt=""
                        aria-hidden="true"
                    >

                    <div
                        v-if="isShowCashOutBetCount"
                        class="count"
                        style="margin-left: -0.4rem;"
                    >
                        {{ cashOutBetCount }}
                    </div>
                </div>
                <p>{{ i18n.t('bottom_function_cash_out') }}</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import JackpotWidget from './jackpot/JackpotWidget.vue';
    import Tooltip from '@/components/Tooltip.vue';
    import MiniBetBuilderBetSlip from '@/components/betSlip/MiniBetBuilderBetSlip.vue';
    import MiniParlayBetSlip from '@/components/betSlip/MiniParlayBetSlip.vue';
    import MiniSingleBetSlip from '@/components/betSlip/Single/MiniSingleBetSlip.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { BetSlipState, BetSlipType, DisplayMode, PlayerType, RoutePage, ZIndex } from '@/interface/enum';
    import Api from '@/libraries/api';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import { useQuickDeposit } from '@/libraries/composables/useQuickDeposit';
    import useScroll from '@/libraries/composables/useScroll';
    import { zIndexMapping } from '@/libraries/helpers/zIndexHelper';
    import { i18n } from '@/libraries/i18n';
    import { getSportsUrl } from '@/libraries/url';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetCountStore } from '@/stores/betCountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const setAutoRefreshFeatureToggle = () => {
        const { autoRefetchFeatureToggleSeconds } = storeToRefs(useFeatureToggleStore());
        const { setFeatureToggle } = useFeatureToggleStore();
        if (autoRefetchFeatureToggleSeconds.value > 0) {
            const intervalId = window.setInterval(async () => {
                const featureToggle = await Api.getConfiguration();
                setFeatureToggle(featureToggle);
            }, autoRefetchFeatureToggleSeconds.value * 1000);

            onUnmounted(() => {
                window.clearInterval(intervalId);
            });
        }
    };

    export default defineComponent({
        name: 'BottomFunction',
        components: {
            Tooltip,
            MiniParlayBetSlip,
            MiniBetBuilderBetSlip,
            MiniSingleBetSlip,
            JackpotWidget,
            SvgIcon,
            SboIcon,
        },
        setup() {
            const { isMixParlayMode, hasSeenCashOutTutorial } = storeToRefs(useRootStore());
            const {
                isCashOutEnabled,
                isCashOutPageEnabled,
                isFirstLoadRefreshBetCountEnabled,
            } = storeToRefs(useFeatureToggleStore());
            const { isEuroSite } = storeToRefs(useFeatureToggleStore());

            const { customerInfo } = storeToRefs(useAccountStore());

            const { sportsFooterTranslateY } = storeToRefs(useHeaderDisplayStore());
            const sportsFooterStyle = computed(() => ({
                'z-index': zIndexMapping[ZIndex.BottomFunction],
                'transition': 'transform .3s',
                'transform': `translateY(${sportsFooterTranslateY.value}px)`,
            }));

            const { runningBetCount, cashOutBetCount } = storeToRefs(useBetCountStore());
            const { refreshBetCount, tick } = useBetCountStore();

            const isShowRunningBetCount = computed(() => runningBetCount.value > 0);
            const isShowCashOutBetCount = computed(() => cashOutBetCount.value > 0);
            const isShowCashOutPage = computed(() => isCashOutEnabled.value && isCashOutPageEnabled.value);

            if (isFirstLoadRefreshBetCountEnabled.value) {
                refreshBetCount(true);
            }

            const { isMiniMpTicketEnabled } = storeToRefs(useFeatureToggleStore());
            const { ticketCount, isValidSubBetCount } = storeToRefs(useParlayBetSlipStore());

            const {
                isShowMediumBetSlip,
                isShowBetBuilderBetSlip,
                isShowParlayBetSlip,
                isShowMiniBetSlip,
                isShowSingleModeBetSlip,
            } = storeToRefs(useBetSlipStore());

            const isShowMiniParlayBetSlip = computed(() => isShowMiniBetSlip.value && isShowParlayBetSlip.value);
            const isShowMiniBetBuilderBetSlip = computed(() => isShowMiniBetSlip.value && isShowBetBuilderBetSlip.value);
            const isShowMiniSingleModeBetSlip = computed(() => isShowMiniBetSlip.value && isShowSingleModeBetSlip.value);

            const showCashOutTooltip = ref(false);
            function displayCashOutTooltip() {
                if (cashOutBetCount.value > 0) {
                    useHeaderDisplayStore().setDisplayMode(DisplayMode.ShowAll);
                    showCashOutTooltip.value = true;
                    useRootStore().setIsCashOutTooltipShowOnce(true);
                }
            }

            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const route = useRoute();
            const router = useRouter();

            function goTo(routeName: RoutePage, path: string) {
                if (route.name !== routeName) {
                    if (isComponentInPp.value) {
                        window.location.href = `${getSportsUrl()}${path}`;
                    } else {
                        router.push({ name: routeName });
                    }
                } else {
                    router.push({ name: RoutePage.Main });
                }
            }

            const { setBetSlipState, setBetSlipType } = useBetSlipStore();

            function clickParlayBetSlip() {
                if (!isMixParlayMode.value) {
                    return;
                }

                if (!isMiniMpTicketEnabled.value) {
                    setBetSlipState(BetSlipState.Big);
                    return;
                }

                if (ticketCount.value > 0) {
                    setBetSlipState(BetSlipState.Medium);
                    setBetSlipType(BetSlipType.Parlay);
                }
            }

            const isCashOutAnimating = ref(false);
            function clickCashOut() {
                isCashOutAnimating.value = false;
                showCashOutTooltip.value = false;
                goTo(RoutePage.CashOut, '/cash-out');
            }

            const shouldShowDepositButton = computed(() => [PlayerType.B2C, PlayerType.B2B2C].includes(customerInfo.value.playerType));

            const depositButtonId = computed(() => (isComponentInPp.value ? 'landing-deposit' : 'sports-deposit'));
            const depositDataGtm = computed(() => (isComponentInPp.value ? 'Landing_Btn_Deposit' : 'SPLobby_Btn_Deposit'));

            const { clickDeposit, loadQuickDepositScript } = useQuickDeposit();

            watch(shouldShowDepositButton, async (newValue) => {
                if (newValue) {
                    await loadQuickDepositScript();
                }
            }, { immediate: true });

            setAutoRefreshFeatureToggle();

            window.eventEmitter.on('tick', tick);
            window.eventEmitter.on('refreshBetCount', refreshBetCount);
            window.eventEmitter.on('displayCashOutTooltip', displayCashOutTooltip);
            onMounted(() => {
                const { initHeaderDisplayHeight } = useScroll();
                initHeaderDisplayHeight(() => document.getElementById('sportsFooterEl'), 'sportsFooterHeight', { observeResize: true });
            });
            onBeforeUnmount(() => {
                window.eventEmitter.off('tick', tick);
                window.eventEmitter.off('refreshBetCount', refreshBetCount);
                window.eventEmitter.off('displayCashOutTooltip', displayCashOutTooltip);
            });

            const showTooltip = ref(false);

            watch(isValidSubBetCount, (newValue, oldValue) => {
                if (oldValue === false && newValue === true) {
                    showTooltip.value = true;
                }
                if (oldValue === true && newValue === false) {
                    showTooltip.value = false;
                }
            });

            const msgTimebox = ref(0);
            watch(showTooltip, (value) => {
                if (value) {
                    clearTimeout(msgTimebox.value);
                    msgTimebox.value = window.setTimeout(() => {
                        showTooltip.value = false;
                    }, 3300);
                }
            });
            watch(showCashOutTooltip, (value) => {
                if (value) {
                    clearTimeout(msgTimebox.value);
                    msgTimebox.value = window.setTimeout(() => {
                        showCashOutTooltip.value = false;
                    }, 10000);
                }
            });

            watch(cashOutBetCount, (newValue, oldValue) => {
                if (!isCashOutAnimating.value && newValue > oldValue) {
                    isCashOutAnimating.value = true;
                    setTimeout(() => {
                        isCashOutAnimating.value = false;
                    }, 4500);
                }
            });

            const { isShowJackpotWidget: _isShowJackpotWidget } = useJackpotDisplay();

            const isShowJackpotWidget = computed(() => _isShowJackpotWidget.value && (!isShowMediumBetSlip.value || isShowMiniBetSlip.value));

            return {
                i18n,
                sportsFooterStyle,
                showCashOutTooltip,
                clickCashOut,
                isMixParlayMode,
                clickParlayBetSlip,
                isMiniMpTicketEnabled,
                showTooltip,
                isEuroSite,
                ticketCount,
                RoutePage,
                goTo,
                isShowRunningBetCount,
                runningBetCount,
                isShowCashOutPage,
                hasSeenCashOutTutorial,
                isCashOutAnimating,
                isShowCashOutBetCount,
                cashOutBetCount,
                isShowJackpotWidget,
                isShowMiniBetSlip,
                isShowMiniParlayBetSlip,
                isShowMiniBetBuilderBetSlip,
                isShowMiniSingleModeBetSlip,
                isShowMediumBetSlip,
                shouldShowDepositButton,
                depositButtonId,
                depositDataGtm,
                clickDeposit,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/tooltip";
    @import "@/scss/components/bottomFunction";
</style>
